<template>
  <section class="section">
    <div class="row">
      <div class="col-lg-12">

        <div class="pagetitle">
          <h1>Транзакции</h1>
          <nav>
            <ol class="breadcrumb">
              <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
              <li class="breadcrumb-item">{{ accountName }}</li>
              <li class="breadcrumb-item active">Транзакции</li>
            </ol>
          </nav>
        </div>

        <div class="card">
          <div class="card-body">

        <div class="row">
          <div :class="{ 'col-lg-8': sumByCategory, 'col-lg-12': !sumByCategory}">
            <h5 class="card-title">{{ accountName }}</h5>
            <TransactionFilterForm
              @applyFilter="applyFilter"
              @clearFilter="clearFilter"
              :initial="filter"
              :categories="categories"
            />
          </div>
          <div class="col-lg-4" v-if="sumByCategory">
            <ChartDonut
                title="Разбивка по категориям"
                :elements="sumByCategory"
            />
            <div class="summary" v-if="summaryType" style="text-align: center">
              <p v-if="summaryType === 'expense'"><span class="fw-bold text-danger"> - {{ getCurrencyFormatted(summaryAmount, accountCurrency) }}</span></p>
              <p v-if="summaryType === 'income'"><span class="fw-bold text-success"> + {{ getCurrencyFormatted(summaryAmount, accountCurrency) }}</span></p>
            </div>
          </div>
        </div>

        <p v-if="loading">Loading...</p>

        <BaseModal
            v-if="isCreateOpen"
            title="Добавить транзакцию"
            @close="closeCreate"
        >
          <TransactionForm
              :initial="initial"
              :categories="categories"
              :error-message="messageError"
              @submit="onAdd"
              @cancel="closeCreate"
              @openMap="openMap"
          />
        </BaseModal>

        <BaseModal
            v-if="isEditOpen && initial"
            title="Изменить транзакцию"
            @close="closeEdit"
        >
          <TransactionForm
              :categories="categories"
              :initial="initial"
              :error-message="messageError"
              @submit="onUpdate"
              @cancel="closeEdit"
              @openMap="openMap"
          />
        </BaseModal>

        <DeleteModal
            title="Подтвердите удаление"
            :isOpen="isOpenDelete"
            @accept="onDelete"
            @close="closeDelete"
        />

        <MapModal
            v-if="isMapOpen && initial.latitude && initial.longitude"
            :latitude="Number(initial.latitude)"
            :longitude="Number(initial.longitude)"
            :selectable="selectable"
            @close="closeMap"
            @select="selectLocation"
        />
          </div>
        </div>

        <div class="col-xxl-12 col-md-6 mb-4">
          <div class="card info-card">
            <div class="card-body">
              <br>
              <button class="btn btn-success" @click="openCreate">Добавить</button>
              <TransactionsTable
                  :transactions="transactions"
                  :accountCurrency="accountCurrency"
                  @update="openEdit"
                  @delete="openDelete"
                  @openMap="openMap"
              />
              <Pagination
                  :page="page"
                  :totalPages="totalPages"
                  :totalElements="totalElements"
                  @onChange="setPage"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTransactions } from '@/features/transactions/composables/useTransactions';
import TransactionsTable from '@/features/transactions/components/TransactionsTable.vue';
import TransactionForm from '@/features/transactions/components/TransactionForm.vue';
import BaseModal from '@/components/ui/components/BaseModal.vue';
import MapModal from '@/components/ui/components/MapModal.vue';
import Pagination from "@/components/ui/components/Pagination.vue";
import TransactionFilterForm from "@/features/transactions/components/TransactionFilterForm.vue";
import ChartDonut from "@/features/accounts/components/ChartDonut.vue";
import DeleteModal from "@/components/ui/components/DeleteModal.vue";
import {getCurrencyFormatted} from "@/api/helpers";

const {
  transactions,
  sumByCategory,
  categories,
  loading,
  isSuccess,
  messageError,
  messageSuccess,
  page,
  size,
  totalPages,
  totalElements,
  summaryAmount,
  summaryType,
  accountName,
  accountCurrency,
  load,
  addTransaction,
  updateTransactionById,
  deleteTransactionById,
} = useTransactions();

const route = useRoute();
const router = useRouter();
const isCreateOpen = ref(false);
const isEditOpen = ref(false);
const initial = reactive({});
const isMapOpen = ref(false);
const selectable = ref(true);
const defaultLatitude = 55.751244;
const defaultLongitude = 37.618423;

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

onMounted(async () => {
  await Promise.all([load(props.accountId, page.value, size.value, filter)]);
});

const filter = reactive({
  categoryId: route.query.categoryId ?? '',
  name: route.query.name ?? '',
  type: route.query.type ?? '',
  amountFrom: route.query.amountFrom ?? '',
  amountTo: route.query.amountTo ?? '',
  createdAtFrom: route.query.createdAtFrom ?? '',
  createdAtTo: route.query.createdAtTo ?? '',
});

watch(
    () => route.query,
    (q) => {
      page.value = Number(q.page ?? 1);
      size.value = Number(q.size ?? 10);
      filter.categoryId = q.categoryId ?? '';
      filter.name = q.name ?? '';
      filter.type = q.type ?? '';
      filter.amountFrom = q.amountFrom ?? '';
      filter.amountTo = q.amountTo ?? '';
      filter.createdAtFrom = q.createdAtFrom ?? '';
      filter.createdAtTo = q.createdAtTo ?? '';

      load(props.accountId, page.value, size.value, filter);
    }
);

function getQuery() {
  const query = {
    page: page.value,
    size: size.value,
  };

  Object.entries(filter).forEach(([key, value]) => {
    if (value !== '' && value != null) {
      query[key] = value;
    }
  });

  return query;
}

async function applyFilter(payload) {
  page.value = 1;
  Object.entries(payload).forEach(([key, value]) => {
    filter[key] = value
  });
  router.replace({
    query: getQuery(),
  });
}

function selectLocation({ latitude, longitude }) {
  initial.latitude = latitude.toFixed(6);
  initial.longitude = longitude.toFixed(6);
  isMapOpen.value = false;
}

function closeMap() {
  isMapOpen.value = false;
  selectable.value = false;
}

async function openMap(coords, editable) {
  isMapOpen.value = true;
  selectable.value = editable;


  const {currentLat, currentLng} = await getUserLocation();
  initial.latitude = coords.latitude ?? currentLat ?? defaultLatitude;
  initial.longitude = coords.longitude ?? currentLng ?? defaultLongitude;
}

function openCreate() {
  isCreateOpen.value = true;
}
function closeCreate() {
  isCreateOpen.value = false;
  for (var field in initial) {
    initial[field] = '';
  }
  messageError.value = "";
}
function openEdit(t) {
  Object.assign(initial, t);
  isEditOpen.value = true;
}
function closeEdit() {
  isEditOpen.value = false;
  for (var field in initial) {
    initial[field] = '';
  }
  messageError.value = "";
}

async function onAdd(payload) {
  await addTransaction(props.accountId, {
    name: payload.name,
    amount: Number(payload.amount || 0),
    categoryId: Number(payload.categoryId),
    createdAt: payload.createdAt,
    latitude: payload.latitude,
    longitude: payload.longitude,
  });
  if (isSuccess.value) {
    closeCreate();
    await setPage(1);
  }
}

async function clearFilter() {
  await load(props.accountId, 1, size.value);
  await router.replace({
    query: {
      page: page.value,
      size: size.value,
    }
  });
}

async function onUpdate(payload) {
  await updateTransactionById(props.accountId, payload.id, {
    name: payload.name,
    amount: Number(payload.amount || 0),
    categoryId: Number(payload.categoryId),
    createdAt: payload.createdAt,
    latitude: payload.latitude,
    longitude: payload.longitude,
  });
  if (isSuccess.value) {
    closeEdit();
    await setPage(page.value);
  }
}

async function getUserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      alert('Геолокация не поддерживается браузером');
      return reject('no_support');
    }

    navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          resolve({
            currentLat: latitude.toFixed(6),
            currentLng: longitude.toFixed(6),
          });
        },
        (err) => {
          resolve({
            currentLat: null,
            currentLng: null,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
    );
  });
}

const isOpenDelete = ref(false);
const deleteObj = ref(null);
function openDelete(payload) {
  isOpenDelete.value = true;
  deleteObj.value = payload
}

function closeDelete() {
  isOpenDelete.value = false;
}

async function onDelete() {
  await deleteTransactionById(props.accountId, deleteObj.value.id);
  deleteObj.value = null;
  isOpenDelete.value = false;
  await setPage(1);
}


const openAction = ref(false);
function toggle() {
  openAction.value = !openAction.value;
}

function handleDocumentClick(e) {
  const filterEl = e.target.closest('.filter');
  if (!filterEl) {
    openAction.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

async function setPage(newPage) {
  if (newPage < 1) return;
  if (totalPages.value && newPage > totalPages.value) return;
  await load(props.accountId, newPage, size.value, filter);
  await router.replace({
    query: getQuery()
  });
}
</script>
